import {useMutation, useQueryClient} from '@github-ui/react-query'
import {PageData} from '@github-ui/pull-request-page-data-tooling/page-data'
import {usePageDataUrl} from '@github-ui/pull-request-page-data-tooling/use-page-data-url'
import {
  fetchWithErrorHandling,
  throwErrorsIfBadResponse,
  parseJSONWithBetterErrors,
} from '@github-ui/pull-request-page-data-tooling/fetch-error-handling'

import {useReviewRequestsQueryKey} from '../components/overview/data/route-queries'
import {useMergeBoxQueryKey} from '@github-ui/mergebox/use-merge-box-query-key'

export type MutationData = {
  reviewerId: number
}

type Callbacks = {
  onError?: (error: Error) => void
}

// This mutation is used to re-request a review from a reviewer who has already been requested.
// It is used when a reviewer has dismissed their review and the author wants to request a review again.
// This duplicates behavior that the mergebox uses to re-request reviews, so we'll need to consolidate these in the future.
export function useReRequestReviewMutation({onError}: Callbacks = {}) {
  const reviewRequestsQueryKey = useReviewRequestsQueryKey()
  const mergeBoxQueryKey = useMergeBoxQueryKey()
  const apiURL = usePageDataUrl(PageData.reRequestReviewFromUser)
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (data: MutationData) => {
      const response = await fetchWithErrorHandling(apiURL, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
        },
        body: data,
      })
      const json = await parseJSONWithBetterErrors(response)
      throwErrorsIfBadResponse(response, json)
      return json
    },
    onSuccess: () => {
      return Promise.all([
        queryClient.invalidateQueries({queryKey: reviewRequestsQueryKey}, {cancelRefetch: false}),
        queryClient.invalidateQueries({queryKey: mergeBoxQueryKey}, {cancelRefetch: false}),
      ])
    },
    onError: (e: Error) => {
      onError?.(e)
    },
  })
}
